import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext.tsx';

interface Props {
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

/** Menu compartilhado dos cards e listas, fora dos contêineres com overflow. */
export const CrmActionsDropdown: React.FC<Props> = ({ anchorRef, onClose, children, className }) => {
  const { isDark } = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0, width: 224, maxHeight: 0 });

  useLayoutEffect(() => {
    const menu = menuRef.current;
    const anchor = anchorRef.current;
    if (!menu || !anchor) return;
    const viewport = window.visualViewport;
    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const leftEdge = (viewport?.offsetLeft || 0) + 8;
      const topEdge = (viewport?.offsetTop || 0) + 8;
      const rightEdge = leftEdge + (viewport?.width || window.innerWidth) - 16;
      const bottomEdge = topEdge + (viewport?.height || window.innerHeight) - 16;
      const width = Math.min(224, Math.max(0, rightEdge - leftEdge));
      const below = Math.max(0, bottomEdge - rect.bottom - 8);
      const above = Math.max(0, rect.top - topEdge - 8);
      const openAbove = menu.scrollHeight > below && above > below;
      const maxHeight = Math.min(bottomEdge - topEdge, openAbove ? above : below);
      const height = Math.min(menu.scrollHeight, maxHeight);
      setPosition({
        left: Math.max(leftEdge, Math.min(rect.right - width, rightEdge - width)),
        top: Math.max(topEdge, Math.min(openAbove ? rect.top - 8 - height : rect.bottom + 8, bottomEdge - height)),
        width, maxHeight,
      });
    };
    const outside = (event: Event) => {
      if (!anchor.contains(event.target as Node) && !menu.contains(event.target as Node)) onClose();
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        anchor.querySelector<HTMLButtonElement>('button')?.focus();
      }
    };
    update();
    document.addEventListener('mousedown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', keyboard);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(anchor);
    observer?.observe(menu);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', keyboard);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [anchorRef, onClose]);

  useLayoutEffect(() => {
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
  }, []);

  return createPortal(
    <div ref={menuRef} role="group" aria-label="Ações do contato" style={position}
      className={clsx('fixed z-[9998] overflow-y-auto overscroll-contain border rounded-xl shadow-xl py-1',
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200', className)}>
      {children}
    </div>, document.body,
  );
};
